/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { Form, FormInstance } from 'antd';

import { PatchUser, UserResponse } from '@/client';
import { ValidationError } from '@/components/Forms/ValidationError';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Input } from '@/primitives/Input';

const requiredMarkAfter = (label: React.ReactNode) => label;

export type UpdateUserFormParams = {
  form: FormInstance<PatchUser>;
  userData: UserResponse;
};

export function UpdateUserForm({ form, userData }: UpdateUserFormParams) {
  const { t } = useMimicTranslation('users');
  const { t: tGlobal } = useMimicTranslation('global');

  return (
    <Form
      validateTrigger="onBlur"
      form={form}
      layout="vertical"
      requiredMark={requiredMarkAfter}
      data-testid="update-user-form"
    >
      <Form.Item
        name="firstName"
        label={t('firstName')}
        initialValue={userData.data.firstName}
        rules={[
          { required: true, message: <ValidationError>{tGlobal('forms.required')}</ValidationError> },
          { type: 'string', message: <ValidationError>{tGlobal('forms.required')}</ValidationError> },
        ]}
      >
        <Input dataTestId="user-first-name" allowClear />
      </Form.Item>
      <Form.Item
        name="lastName"
        label={t('lastName')}
        initialValue={userData.data.lastName}
        rules={[
          { required: true, message: <ValidationError>{tGlobal('forms.required')}</ValidationError> },
          { type: 'string', message: <ValidationError>{tGlobal('forms.required')}</ValidationError> },
        ]}
      >
        <Input dataTestId="user-last-name" allowClear />
      </Form.Item>
      <Form.Item
        name="email"
        label={t('email')}
        initialValue={userData.data.email}
        rules={[
          { required: true, message: <ValidationError>{t('invite.enterValidEmail')}</ValidationError> },
          { min: 1, message: <ValidationError>{t('invite.enterValidEmail')}</ValidationError> },
          { type: 'email', message: <ValidationError>{t('invite.enterValidEmail')}</ValidationError> },
        ]}
      >
        <Input dataTestId="user-email" allowClear />
      </Form.Item>
    </Form>
  );
}
